import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AdminPanelNew.css';
import './adminMobileShared.css';
import './adminDarkTheme.css';
import useAnalytics from './hooks/useAnalytics';
import useMenuCounts from './hooks/useMenuCounts';
import useAdminLogin from './hooks/useAdminLogin';
import { getAdminUser, setAdminUser, removeAdminToken, isAdminLoggedIn } from './utils/authUtils';
import { getApiBaseUrlWithoutPath } from '../config/environment';
import {
  hasAccessToView,
  getFirstAccessibleView,
  VIEW_TO_PERMISSION,
} from './constants/teamAccessPermissions';
import { shouldSuppressAdminAccessDeniedToast } from './utils/adminApiError';

// Import all components
import Dashboard from './components/Dashboard';
import ProductPdpViews from './components/ProductPdpViews';
import Products from './components/Products';
import Orders from './components/Orders';
import Analytics from './components/Analytics';
import TeamSales from './components/TeamSales';
import Users from './components/Users';
import UserManagement from './components/UserManagement';
import Categories from './components/Categories';
import Revenue from './components/Revenue';
import Settings from './components/Settings';
import MyAccount from './components/MyAccount';
import AddNewProduct from './components/AddNewProduct';
import AccessDeniedToast from './components/AccessDeniedToast';
import AdminNotificationsBell from './components/AdminNotificationsBell';
import AdminGlobalSearch from './components/AdminGlobalSearch';
import useSiteBranding from '../hooks/useSiteBranding';
import { readCreatorStorefrontFromStorage } from '../utils/creatorStorefront';
import { useIdleSessionTimeout, ADMIN_SESSION_IDLE_MS } from '../hooks/useIdleSessionTimeout';
import { getMongoProductId } from './utils/productMongoId';
import ThemeModeIconButton from '../components/ThemeModeIconButton';

const MOBILE_MAX = '(max-width: 768px)';

function resolveAdminMobileBarTitle(user, siteName) {
  if (!user) return 'Admin';
  if (user.role === 'admin') return 'Admin';
  const uid = user._id || user.id;
  const stored = uid ? readCreatorStorefrontFromStorage(uid) : null;
  const shopTitle =
    (stored?.headline && String(stored.headline).trim()) ||
    (user.creatorHeadline && String(user.creatorHeadline).trim()) ||
    (siteName && String(siteName).trim()) ||
    (user.name && String(user.name).trim()) ||
    'Shop';
  return shopTitle;
}

/** Three-line hamburger for mobile menu toggle */
function AdminHamburgerIcon() {
  return (
    <svg
      className="admin-hamburger-icon"
      width="22"
      height="18"
      viewBox="0 0 22 18"
      aria-hidden
      focusable="false"
    >
      <rect width="22" height="2" rx="1" fill="currentColor" />
      <rect y="8" width="22" height="2" rx="1" fill="currentColor" />
      <rect y="16" width="22" height="2" rx="1" fill="currentColor" />
    </svg>
  );
}

const AdminPanelNew = () => {
  const { siteName, siteLogoUrl } = useSiteBranding();
  const [currentView, setCurrentView] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_MAX).matches : false
  );
  // Webview/desktop: sidebar is a permanent rail (always open).
  // Mobile: sidebar is an overlay drawer that opens via the hamburger button.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !window.matchMedia(MOBILE_MAX).matches;
  });
  
  // Dashboard data for real-time counts - only fetch when authenticated
  const { data: dashboardData, loading: analyticsLoading, refetch: refetchAnalytics } = useAnalytics();
  const refetchAnalyticsRef = useRef(refetchAnalytics);
  refetchAnalyticsRef.current = refetchAnalytics;
  
  // Fallback counts for menu items
  const { counts: menuCounts, loading: countsLoading, refetch: refetchCounts } = useMenuCounts();
  const refetchCountsRef = useRef(refetchCounts);
  refetchCountsRef.current = refetchCounts;
  
  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [navigationParams, setNavigationParams] = useState({});
  const [accessDeniedMessage, setAccessDeniedMessage] = useState(null);
  /** Sidebar Products badge — synced from Products page (catalog total). */
  const [sidebarProductCount, setSidebarProductCount] = useState(null);
  
  // Admin login hook
  const { login: adminLogin, loading: loginLoading, error: loginError, clearError } = useAdminLogin();

  // Add admin-page class to body when component mounts
  useEffect(() => {
    document.body.classList.add('admin-page');
    return () => {
      document.body.classList.remove('admin-page');
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MAX);
    const onChange = () => {
      const mobile = mq.matches;
      setIsMobile(mobile);
      // Desktop must always show the rail; mobile must close the drawer
      // when the viewport flips so it doesn't stay stuck open over content.
      setSidebarOpen(!mobile);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Get the best available count (analytics first, then fallback)
  const getCount = (analyticsKey, fallbackKey) => {
    if (analyticsLoading || countsLoading) return '...';
    
    // For users, always use active users count from menuCounts (not total)
    if (analyticsKey === 'totalUsers') {
      const val = menuCounts[fallbackKey];
      return (typeof val === 'number' || typeof val === 'string') ? val : 0;
    }
    
    if (analyticsKey === 'totalProducts') {
      if (sidebarProductCount != null) return sidebarProductCount;
      const menuVal = menuCounts.totalProducts;
      if (typeof menuVal === 'number' && menuVal >= 0) return menuVal;
    }

    // For team members: use menuCounts for orders (APIs filter by createdBy; analytics may not)
    if (currentUser?.role === 'team' && analyticsKey === 'totalOrders') {
      const val = menuCounts[fallbackKey];
      return (typeof val === 'number' || typeof val === 'string') ? val : 0;
    }
    
    // Try analytics data first (ensure we never return objects - React can't render them)
    const analyticsCount = dashboardData?.[analyticsKey] ?? dashboardData?.[`${analyticsKey}Count`];
    if (analyticsCount !== undefined && analyticsCount !== null) {
      const fallbackVal = menuCounts[fallbackKey];
      return (typeof analyticsCount === 'number' || typeof analyticsCount === 'string')
        ? analyticsCount
        : (typeof fallbackVal === 'number' || typeof fallbackVal === 'string' ? fallbackVal : 0);
    }
    
    // Fallback to individual counts (never return objects)
    const rawFallback = menuCounts[fallbackKey];
    return (typeof rawFallback === 'number' || typeof rawFallback === 'string')
      ? rawFallback
      : 0;
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fas fa-tachometer-alt', count: null },
    { 
      id: 'products', 
      label: 'Products', 
      icon: 'fas fa-box', 
      count: getCount('totalProducts', 'totalProducts')
    },
    { 
      id: 'orders', 
      label: 'Orders', 
      icon: 'fas fa-shopping-cart', 
      count: getCount('totalOrders', 'totalOrders')
    },
    { id: 'analytics', label: 'Analytics', icon: 'fas fa-chart-line', count: null },
    { id: 'sales', label: 'Sales', icon: 'fas fa-coins', count: null },
    { 
      id: 'users', 
      label: 'Users', 
      icon: 'fas fa-users', 
      count: getCount('totalUsers', 'totalUsers')
    },
    { 
      id: 'categories', 
      label: 'Categories', 
      icon: 'fas fa-tags', 
      count: getCount('totalCategories', 'totalCategories')
    },
    { id: 'revenue', label: 'Revenue & Accounts', icon: 'fas fa-dollar-sign', count: null },
    { id: 'settings', label: 'Settings', icon: 'fas fa-cog', count: null },
    { id: 'account', label: 'My Account', icon: 'fas fa-user', count: null }
  ];

  const visibleMenuItems =
    currentUser?.role === 'team'
      ? menuItems.filter((item) =>
          hasAccessToView(item.id, currentUser?.accessPermissions, true)
        )
      : menuItems;

  const mobileBarTitle = resolveAdminMobileBarTitle(currentUser, siteName);

  // Listen for profile update - refresh sidebar user info
  useEffect(() => {
    const onProfileUpdated = (e) => {
      const updated = e.detail || JSON.parse(localStorage.getItem('adminUser') || '{}');
      if (updated && Object.keys(updated).length > 0) {
        setCurrentUser(prev => prev ? { ...prev, ...updated } : updated);
      }
    };
    window.addEventListener('adminProfileUpdated', onProfileUpdated);
    return () => window.removeEventListener('adminProfileUpdated', onProfileUpdated);
  }, []);

  useEffect(() => {
    const onProductMenuCount = (e) => {
      const n = Number(e.detail?.count);
      if (Number.isFinite(n) && n >= 0) setSidebarProductCount(n);
    };
    window.addEventListener('adminProductMenuCount', onProductMenuCount);
    window.addEventListener('adminTeamProductMenuCount', onProductMenuCount);
    return () => {
      window.removeEventListener('adminProductMenuCount', onProductMenuCount);
      window.removeEventListener('adminTeamProductMenuCount', onProductMenuCount);
    };
  }, []);

  // Listen for 401 from admin API only — do NOT tie to customer authExpired (would clear admin session wrongly)
  useEffect(() => {
    const onAdminLoggedOut = () => {
      removeAdminToken();
      setIsAuthenticated(false);
      setCurrentUser(null);
      setCurrentView('dashboard');
    };
    window.addEventListener('adminLoggedOut', onAdminLoggedOut);
    return () => {
      window.removeEventListener('adminLoggedOut', onAdminLoggedOut);
    };
  }, []);

  // Listen for 403 Access Denied from API (backend permission check)
  useEffect(() => {
    const onAccessDenied = (e) => {
      const { error, permissionKey } = e.detail || {};
      if (shouldSuppressAdminAccessDeniedToast(permissionKey, error)) return;
      const viewKey = VIEW_TO_PERMISSION?.[currentView];
      const blockedCurrentView =
        permissionKey && viewKey && String(permissionKey) === String(viewKey);
      if (currentUser?.role === 'team' && blockedCurrentView) {
        const firstView = getFirstAccessibleView(currentUser?.accessPermissions, true);
        setCurrentView(firstView);
      }
      if (error) setAccessDeniedMessage(error);
    };
    window.addEventListener('adminAccessDenied', onAccessDenied);
    return () => window.removeEventListener('adminAccessDenied', onAccessDenied);
  }, [currentUser?.role, currentUser?.accessPermissions]);

  // Redirect team members away from restricted views (use actual accessPermissions)
  useEffect(() => {
    if (currentUser?.role === 'team') {
      const access = currentUser?.accessPermissions;
      if (!hasAccessToView(currentView, access, true)) {
        const firstView = getFirstAccessibleView(access, true);
        setCurrentView(firstView);
      }
    }
  }, [currentUser?.role, currentUser?.accessPermissions, currentView]);

  const handleAdminIdleLogout = useCallback(() => {
    if (!localStorage.getItem('adminToken')) return;
    removeAdminToken();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentView('dashboard');
    if (window.showToast) {
      window.showToast('Admin session ended due to inactivity. Please sign in again.', 'info');
    }
  }, []);

  useIdleSessionTimeout({
    enabled: isAuthenticated,
    idleMs: ADMIN_SESSION_IDLE_MS,
    onIdle: handleAdminIdleLogout
  });

  // Check authentication once on mount (refs avoid re-running when refetch identities change)
  useEffect(() => {
    const checkAuth = () => {
      console.log('🔍 Checking admin authentication...');
      const isLoggedIn = isAdminLoggedIn();
      console.log('🔍 Admin logged in:', isLoggedIn);
      
      if (isLoggedIn) {
        const user = getAdminUser();
        if (user) {
          setIsAuthenticated(true);
          setCurrentUser(user);
          setTimeout(() => {
            if (user?.role !== 'team') {
              refetchAnalyticsRef.current();
            }
            refetchCountsRef.current();
          }, 100);
          console.log('✅ Admin authenticated:', user?.role || 'admin', user?.name);
        } else {
          // Token exists but no user in storage - clear and show login
          removeAdminToken();
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } else {
        // Not logged in - ensure we show login form
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    };
    
    checkAuth();
  }, []);

  // Fetch fresh admin profile for team members (to get updated accessPermissions after admin changes)
  const hasFetchedTeamProfile = React.useRef(false);
  useEffect(() => {
    if (currentUser?.role !== 'team' || !isAdminLoggedIn() || hasFetchedTeamProfile.current) return;
    hasFetchedTeamProfile.current = true;
    const fetchFreshProfile = async () => {
      try {
        const apiUrl = getApiBaseUrlWithoutPath();
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${apiUrl}/api/admin/profile`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.profile) {
            const freshUser = { ...currentUser, ...data.profile, accessPermissions: data.profile.accessPermissions ?? currentUser?.accessPermissions };
            setAdminUser(freshUser);
            setCurrentUser(freshUser);
          }
        }
      } catch (_) { /* ignore - use cached user */ }
    };
    fetchFreshProfile();
  }, [currentUser?.role]);

  // Remove loadProducts function as it's now handled by hooks

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    
    // Clear any previous errors
    if (loginError) {
      clearError();
    }
    
    if (!email.trim() || !password.trim()) {
      alert('Please enter both email and password');
      return;
    }

    try {
      console.log('🔧 Attempting admin login...');
      
      const response = await adminLogin({
        email: email.trim(),
        password: password
      });
      
      console.log('🔧 Admin login response:', response);
      
      if (response.success && response.token) {
        // Verify token is stored before proceeding
        const token = localStorage.getItem('adminToken');
        if (!token) {
          console.error('❌ Token not stored after login');
          throw new Error('Failed to store authentication token');
        }
        
        setIsAuthenticated(true);
        setCurrentUser(response.user);
        setEmail('');
        setPassword('');
        
        // Fetch analytics data after successful login with delay to ensure token is ready
        setTimeout(() => {
          // Double-check authentication before fetching
          if (isAdminLoggedIn()) {
            if (response.user?.role !== 'team') {
              refetchAnalytics();
            }
            refetchCounts();
          } else {
            console.warn('⚠️ Admin not authenticated after login, skipping data fetch');
          }
        }, 500); // Increased delay to ensure token is properly stored
        
        console.log('✅ Admin login successful');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      // Error is already handled by the useAdminLogin hook
    }
  };

  const handleLogout = () => {
    removeAdminToken();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentView('dashboard');
    
    console.log('✅ Admin logged out successfully');
  };

  const handleEditProduct = (product) => {
    console.log('Setting product for editing:', product);

    const mongoId = getMongoProductId(product);

    // Ensure MongoDB _id is used for PUT /api/admin/products/:id (not SKU-style productId)
    const productWithId = {
      ...product,
      ...(mongoId ? { id: mongoId, _id: mongoId } : {}),
    };
    
    console.log('Product with ID:', productWithId);
    console.log('Setting editingProduct state to:', productWithId);
    setEditingProduct(productWithId);
    setCurrentView('add-product');
    console.log('Current view set to add-product');
  };

  const handleNavigation = (view, params = {}) => {
    console.log('🔄 Navigation to:', view, 'with params:', params);
    console.log('🔄 Current editingProduct before navigation:', editingProduct);
    setCurrentView(view);
    
    // Refresh menu counts when visiting products/orders (keeps team member counts accurate)
    if (view === 'products' || view === 'orders') {
      refetchCounts();
    }
    
    // Store navigation params for components to use
    if (params && Object.keys(params).length > 0) {
      setNavigationParams(params);
    } else {
      setNavigationParams({});
    }
    
    // Clear editing product when navigating away from add-product
    if (view !== 'add-product') {
      console.log('🔄 Clearing editingProduct - navigating away from add-product');
      setEditingProduct(null);
    } else {
      console.log('🔄 Keeping editingProduct - navigating to add-product');
    }
    // Only auto-close the sidebar on mobile (desktop keeps the permanent rail).
    if (isMobile) setSidebarOpen(false);
  };

  const renderCurrentView = () => {
    // Enforce team member access - don't render restricted views even if state is stale
    const isTeam = currentUser?.role === 'team';
    const effectiveView =
      isTeam && !hasAccessToView(currentView, currentUser?.accessPermissions, true)
        ? getFirstAccessibleView(currentUser?.accessPermissions, true)
        : currentView;

    switch (effectiveView) {
      case 'dashboard':
        console.log('📊 Rendering Dashboard component');
        return <Dashboard currentUser={currentUser || {}} onNavigate={handleNavigation} />;
      case 'products':
        return <Products 
          currentUser={currentUser}
          onNavigate={(view) => handleNavigation(view)} 
          onEditProduct={handleEditProduct}
          navigationParams={navigationParams}
        />;
      case 'orders':
        return <Orders currentUser={currentUser} navigationParams={navigationParams} />;
      case 'pdp-views':
        return (
          <ProductPdpViews
            currentUser={currentUser}
            onNavigate={handleNavigation}
            onEditProduct={handleEditProduct}
          />
        );
      case 'analytics':
        return <Analytics currentUser={currentUser} />;
      case 'sales':
        return <TeamSales currentUser={currentUser} />;
      case 'users':
        return <UserManagement navigationParams={navigationParams} />;
      case 'categories':
        return <Categories />;
      case 'revenue':
        return <Revenue currentUser={currentUser} />;
      case 'settings':
        return <Settings />;
      case 'account':
        return <MyAccount currentUser={currentUser || {}} onLogout={handleLogout} />;
      case 'add-product':
        return <AddNewProduct 
          onNavigate={(view) => handleNavigation(view)} 
          editingProduct={editingProduct}
          onEditComplete={() => {
            console.log('🔄 onEditComplete called - clearing editingProduct');
            setEditingProduct(null);
          }}
          currentUser={currentUser}
        />;
      default:
        return <Dashboard currentUser={currentUser || {}} onNavigate={handleNavigation} />;
    }
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="logo-section">
              <i className="fas fa-store"></i>
              <div className="header-text">
                <h1>Super Wear</h1>
                <p>Admin Panel</p>
              </div>
            </div>
          </div>
          <div className="login-body">
            <h2>Welcome Back</h2>
            <p>Please sign in to your admin account</p>
            <form onSubmit={handleLogin} className="login-form">
              {/* Error Message */}
              {loginError && (
                <div className="login-error">
                  <i className="fas fa-exclamation-circle"></i>
                  {loginError}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (loginError) clearError();
                  }}
                  placeholder="Enter your email"
                  required
                  disabled={loginLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="admin-password-field-wrap">
                  <input
                    type={showAdminPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (loginError) clearError();
                    }}
                    className="admin-password-input"
                    placeholder="Enter your password"
                    required
                    disabled={loginLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="admin-password-toggle"
                    onClick={() => setShowAdminPassword((v) => !v)}
                    disabled={loginLoading}
                    aria-label={showAdminPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fas ${showAdminPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                  </button>
                </div>
              </div>
              
              <div className="form-options">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loginLoading}
                  />
                  <span className="checkmark"></span>
                  Remember me
                </label>
              </div>
              
              <button 
                type="submit" 
                className="login-btn"
                disabled={loginLoading}
              >
                {loginLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Signing In...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i>
                    Sign In
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel-new">
      <AccessDeniedToast
        message={accessDeniedMessage}
        visible={!!accessDeniedMessage}
        onClose={() => setAccessDeniedMessage(null)}
      />
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className={`sidebar-header${isMobile ? ' sidebar-header--mobile-drawer' : ''}`}>
          {isMobile ? (
            <>
              <button
                type="button"
                className="sidebar-toggle sidebar-toggle--drawer-close"
                onClick={() => setSidebarOpen(false)}
                aria-expanded={sidebarOpen}
                aria-label="Close menu"
              >
                <i className="fas fa-times" aria-hidden />
              </button>
              <p className="admin-sidebar-drawer-title" title={siteName}>
                {siteName}
              </p>
              <ThemeModeIconButton className="admin-sidebar-theme-toggle" />
            </>
          ) : (
            <>
              <div className="logo">
                {siteLogoUrl ? (
                  <img src={siteLogoUrl} alt="" className="admin-sidebar-logo" />
                ) : (
                  <i className="fas fa-store"></i>
                )}
                <span className="logo-text">{siteName}</span>
              </div>
              <ThemeModeIconButton className="admin-sidebar-theme-toggle admin-sidebar-theme-toggle--rail" />
              <button
                type="button"
                className="sidebar-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-expanded={sidebarOpen}
                aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              >
                <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`} aria-hidden />
              </button>
            </>
          )}
        </div>

        <nav className="sidebar-nav">
          <ul className="nav-list">
            {visibleMenuItems.map((item) => (
              <li key={item.id} className="nav-item">
                <button
                  className={`nav-link ${currentView === item.id ? 'active' : ''}`}
                  type="button"
                  onClick={() => handleNavigation(item.id)}
                >
                  <i className={item.icon}></i>
                  <span className="nav-label">{item.label}</span>
                  {item.count !== null && (
                    <span className="nav-count">{item.count}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar-placeholder">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-details">
              <div className="user-name">{currentUser.name}</div>
              <div className="user-role">{currentUser.role}</div>
            </div>
          </div>
          <button
            type="button"
            className="logout-btn"
            onClick={() => {
              if (isMobile) setSidebarOpen(false);
              handleLogout();
            }}
          >
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Top Bar */}
        <div className="top-bar">
          <div className="top-bar-left">
            {/* Hamburger only on mobile — desktop has a permanent sidebar rail. */}
            {isMobile && (
              <button
                type="button"
                className="mobile-menu-btn"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-expanded={sidebarOpen}
                aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              >
                {sidebarOpen ? <i className="fas fa-times" aria-hidden /> : <AdminHamburgerIcon />}
              </button>
            )}
            {!isMobile && (
              <div className="breadcrumb">
                <span className="breadcrumb-item">Admin</span>
                <i className="fas fa-chevron-right"></i>
                <span className="breadcrumb-current">
                  {visibleMenuItems.find((item) => item.id === currentView)?.label ||
                    menuItems.find((item) => item.id === currentView)?.label ||
                    'Dashboard'}
                </span>
              </div>
            )}
          </div>

          {isMobile && (
            <div className="admin-top-bar-mobile-title" title={mobileBarTitle}>
              {mobileBarTitle}
            </div>
          )}
          
          <div className="top-bar-right">
            <div className="top-bar-actions">
              <AdminNotificationsBell currentUser={currentUser} onNavigate={handleNavigation} />
              <AdminGlobalSearch
                currentUser={currentUser}
                navItems={visibleMenuItems.map(({ id, label }) => ({ id, label }))}
                onNavigate={handleNavigation}
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="content-area">
          {renderCurrentView()}
        </div>
      </div>

      {/* Sidebar overlay backdrop — only on mobile while the drawer is open. */}
      {isMobile && sidebarOpen && (
        <div
          className="mobile-overlay sidebar-overlay-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}
    </div>
  );
};

export default AdminPanelNew;
